import { useAppSelector } from "../store/store";
import { useGenerateBridgeTokenMutation, useLazyGetMarketingResourcePublicUrlsQuery } from "../services/api";
import { config } from "../configs";
import { tracker } from "../services/mixpanel";
import { FileType, PurlModule } from "../utils/documentsModules";
import { toast } from "react-toastify";
import { showNotification } from "../services/showNotification";

const solarStudyUrl = config?.REACT_APP_SOLAR_STUDY_URL || "https://solar.study";

interface IResourceClickHandlerProps {
  moduleName: PurlModule;
  openAnimatedOverview?: (url: string) => void;
  handleVideoModal?: (name: string, link: string) => void;
  handlePdfModal?: (name: string, link: string) => void;
  handleDocumentModal?: (id: string, heading: string, showSidebar?: boolean, message?: string, title?: string, key?: string) => void;
}

export const useResourceClickHandler = ({
  moduleName,
  openAnimatedOverview,
  handleVideoModal,
  handlePdfModal,
  handleDocumentModal,
}: IResourceClickHandlerProps) => {
  const userEmail = useAppSelector((state) => state.login.email);
  const [generateBridgeToken] = useGenerateBridgeTokenMutation();
  const [fetchPublicUrls] = useLazyGetMarketingResourcePublicUrlsQuery();

  const handleSolarStudyRedirect = async (url: string): Promise<void> => {
    tracker.dashboard.additionalResources.solarStudy.onClick();
    const solarStudyRedirectUrl = url ? url : solarStudyUrl;
    try {
      if (!userEmail) {
        window.open(solarStudyRedirectUrl, "_blank");
        return;
      }

      const response = await generateBridgeToken(userEmail).unwrap();

      if (response.success && response.data?.bridgeToken) {
        window.open(
          `${solarStudyRedirectUrl}/cross-auth?token=${response.data.bridgeToken}`,
          "_blank"
        );
      } else {
        window.open(solarStudyRedirectUrl, "_blank");
      }
    } catch (error) {
      console.error(
        "Error redirecting to Solar Study:",
        error instanceof Error ? error.message : "Unknown error"
      );
      window.open(solarStudyRedirectUrl, "_blank");
    }
  };

  const handleResourceClick = (item: IPurlMarketingResource) => {
    if (item.type === FileType.SOLAR_STUDY) {
      handleSolarStudyRedirect(item.input || solarStudyUrl);
      return;
    }

    if (!item.input) {
      if (item.message) {
        toast.info(item.message);
      }
      return;
    }

    // Dynamic mixpanel event as requested
    if (item.title) {
      if (moduleName === PurlModule.ADDITIONAL_RESOURCES) {
        tracker.dashboard.additionalResources.dynamicItem.onClick(item.title);
      } else if (moduleName === PurlModule.OVERVIEW) {
        tracker.dashboard.overview.dynamicItem.onClick(item.title);
      }
    }
    const handleFileAsset = async (item: IPurlMarketingResource) => {
      try {
        const result = await fetchPublicUrls([item.input]).unwrap();
        const publicUrl = result?.data?.[0]?.url;
        console.log("publicUrl", publicUrl, "handlePdfModal", handlePdfModal, "publicUrl && handlePdfModal", publicUrl && handlePdfModal)
        if (publicUrl && handlePdfModal) {
          handlePdfModal(item.title, publicUrl);
        } else if (publicUrl) {
          window.open(publicUrl, "_blank");
        } else {
          showNotification("error", "Failed to load the file.");
        }
      } catch (err) {
        console.error("Failed to fetch public URL for file:", err);
        showNotification("error", "Failed to load the file.");
      }
    };

    switch (item.type) {
      case FileType.VIDEO:
        if (handleVideoModal) {
          handleVideoModal(item.title, item.input);
        }
        break;
      //Todo TO VERIFY THE CHANGES implemented with the get public Url link when the file type is file
      case FileType.File: {
        // input is an S3 key — resolve to public URL first, then open as PDF
        handleFileAsset(item);
        break;
      }
      case FileType.PDF:
        if (handlePdfModal) {
          handlePdfModal(item.title, item.input);
        }
        break;
      case FileType.GDRIVE_FOLDER:
        if (handleDocumentModal) {
          const showSidebar = item.key === "sampleAssetDocuments" ? undefined : false
          handleDocumentModal(item.input, item.title, showSidebar, item.message, item.title);
        }
        break;
      case FileType.EXTERNAL:
        window.open(item.input, "_blank");
        break;
      default:
        window.open(item.input, "_blank");
        break;
    }
  };

  return { handleResourceClick, handleSolarStudyRedirect };
};
